import { FieldWrapper, Textarea } from '@dixa/lib-shared-habla';
import { ComponentPropsWithRef, useEffect, useState } from 'react';
import { FieldPath, FieldValues } from 'react-hook-form';

import {
  HookFormFieldWrappedBaseProps,
  useHookFormFieldWrapper,
} from 'utils/hookForm';

export type TextareaProps = {
  showCharacterLimit?: number;
} & ComponentPropsWithRef<typeof Textarea>;

/** Represents the React Hook Form variant of the {@link Textarea} component. */
export const HookFormTextarea = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: HookFormFieldWrappedBaseProps<
    TFieldValues,
    TName,
    Partial<TextareaProps>
  >,
): JSX.Element => {
  const { controller, controllerProps, fieldWrapperProps } =
    useHookFormFieldWrapper(props);

  const { showCharacterLimit, ...textareaProps } = props.componentProps ?? {};

  const [textLength, setTextLength] = useState<number>(0);

  useEffect(() => {
    if (showCharacterLimit) {
      if (controller.field.value.length) {
        setTextLength(controller.field.value.length);
      }
    }
  }, []);

  return (
    <FieldWrapper
      {...fieldWrapperProps}
      description={
        showCharacterLimit
          ? `${showCharacterLimit - textLength} characters left`
          : fieldWrapperProps.description
      }
    >
      <Textarea
        {...textareaProps}
        {...controller.field}
        id={controllerProps.name}
        aria-disabled={fieldWrapperProps.disabled}
        disabled={fieldWrapperProps.disabled}
        aria-required={fieldWrapperProps.required}
        required={fieldWrapperProps.required}
        onChange={(event) => {
          if (showCharacterLimit) {
            setTextLength(event.target.value.length);
          }
          controller.field.onChange(event.target.value);
        }}
        status={FieldWrapper.getStatus(fieldWrapperProps.meta)}
      />
    </FieldWrapper>
  );
};
